import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Building2, Users, CalendarClock, MapPin, FileText, Settings, LogOut, Menu, ShieldCheck, UserCog, ExternalLink, Layout } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/properties", label: "Properties", icon: Building2 },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/site-visits", label: "Site Visits", icon: CalendarClock },
  { to: "/admin/locations", label: "Locations", icon: MapPin },
  { to: "/admin/blog", label: "AI Blog", icon: FileText },
  { to: "/admin/pages", label: "Landing Pages", icon: Layout },
  { to: "/admin/team", label: "Team", icon: UserCog },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function Nav({ user, logout, nav }) {
  return (
    <div className="flex flex-col h-full">
      <Link to="/admin" className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-white" /></div>
        <span className="font-heading font-bold text-white">Admin Portal</span>
      </Link>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
            <l.icon className="w-4 h-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800"><ExternalLink className="w-4 h-4" /> View Site</a>
        <div className="px-3 py-2 text-xs text-slate-400">{user?.name} · {user?.role}</div>
        <button onClick={() => { logout(); nav("/login"); }} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-rose-300 hover:bg-slate-800 w-full"><LogOut className="w-4 h-4" /> Logout</button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-slate-900"><Nav user={user} logout={logout} nav={nav} /></aside>
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-slate-900 h-14 flex items-center justify-between px-4">
        <span className="font-heading font-bold text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-400" /> Admin</span>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button className="text-white" data-testid="admin-mobile-menu"><Menu className="w-6 h-6" /></button></SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 bg-slate-900 border-slate-800"><Nav user={user} logout={logout} nav={nav} /></SheetContent>
        </Sheet>
      </div>
      <main className="lg:pl-64 pt-14 lg:pt-0"><div className="p-4 sm:p-6 lg:p-8"><Outlet /></div></main>
    </div>
  );
}
